import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import * as R from 'ramda';
import Table from '@material-ui/core/Table';
import TableRow from '@material-ui/core/TableRow';
import TableCell from '@material-ui/core/TableCell';
import Lens from '@material-ui/icons/Lens';
import { withBlank } from '../utils';
import { useStyles } from './styles';
import { getMaxValuesLength, getEmptyValues, getLabel, valueLabel, maxCount } from './lib';
var patern = {
  id: PropTypes.string,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  count: PropTypes.number
};
var Header = function Header(_ref) {
  var header = _ref.header,
    colSpan = _ref.colSpan,
    valuesSpan = _ref.valuesSpan;
  var classes = useStyles();
  return R.map(function (_ref2) {
    var id = _ref2.id,
      label = _ref2.label,
      count = _ref2.count;
    return React.createElement(TableRow, {
      key: id,
      className: cx(classes.header, classes.border)
    }, React.createElement(TableCell, {
      tabIndex: 0,
      className: cx(classes.cell, classes.headerTitleCell),
      colSpan: colSpan,
      scope: 'row',
      title: label
    }, label), R.times(function (index) {
      return React.createElement(TableCell, {
        key: id + '-' + index,
        className: cx(classes.border, classes.cell, classes.headerCell),
        scope: 'col'
      }, index < count ? valueLabel : '');
    }, valuesSpan));
  })(header);
};
Header.propTypes = process.env.NODE_ENV !== "production" ? {
  header: PropTypes.array,
  colSpan: PropTypes.number,
  valuesSpan: PropTypes.number
} : {};
var RowHeader = function RowHeader(_ref3) {
  var _ref3$id = _ref3.id,
    id = _ref3$id === undefined ? 'rowHeader' : _ref3$id,
    rows = _ref3.rows,
    emptyValues = _ref3.emptyValues;
  var classes = useStyles();
  return React.createElement(TableRow, null, R.map(function (_ref4) {
    var id = _ref4.id,
      label = _ref4.label;
    return React.createElement(TableCell, {
      tabIndex: 0,
      key: id,
      className: cx(classes.rows, classes.border, classes.cell, classes.rowheaderCell),
      title: label
    }, label);
  })(rows), React.createElement(TableCell, {
    className: cx(classes.separatorValues, classes.border, classes.separatorSize)
  }), R.addIndex(R.map)(function (value, index) {
    return React.createElement(TableCell, {
      key: id + '-' + index,
      className: cx(classes.separatorValues, classes.border, classes.cell)
    });
  })(emptyValues));
};
RowHeader.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  rows: PropTypes.array,
  emptyValues: PropTypes.array
} : {};
var Sections = function Sections(_ref5) {
  var sections = _ref5.sections,
    colSpan = _ref5.colSpan;
  var classes = useStyles();
  return React.createElement(TableRow, {
    className: cx(classes.border)
  }, React.createElement(TableCell, {
    tabIndex: 0,
    className: cx(classes.border, classes.sections),
    colSpan: colSpan
  }, R.addIndex(R.map)(function (_ref6, index) {
    var id = _ref6.id,
      label = _ref6.label;
    return React.createElement(Fragment, {
      key: id
    }, React.createElement('span', {
      className: classes.sectionsTitleCell
    }, label + ': '), React.createElement('span', {
      className: classes.sectionsCell
    }, valueLabel, index === sections.length - 1 ? null : React.createElement(Lens, {
      className: classes.icon
    })));
  })(sections)));
};
Sections.propTypes = process.env.NODE_ENV !== "production" ? {
  sections: PropTypes.array,
  colSpan: PropTypes.number
} : {};
var RowValues = function RowValues(_ref7) {
  var _ref7$id = _ref7.id,
    id = _ref7$id === undefined ? 'rowValues' : _ref7$id,
    rows = _ref7.rows,
    emptyValues = _ref7.emptyValues;
  var classes = useStyles();
  var totalMaxCount = R.min(maxCount, R.reduce(R.max, 0, R.pluck('count', rows)));
  return R.times(function (rowIndex) {
    return React.createElement(TableRow, {
      key: id + '-' + rowIndex
    }, R.map(function (_ref8) {
      var id = _ref8.id,
        count = _ref8.count;
      return React.createElement(TableCell, {
        key: id + '-' + rowIndex,
        className: cx(classes.rows, classes.border, classes.cell)
      }, rowIndex < count ? valueLabel : '');
    })(rows), React.createElement(TableCell, {
      className: cx(classes.separatorValues, classes.separatorSize, classes.border)
    }), R.times(function (columnIndex) {
      return React.createElement(TableCell, {
        key: rowIndex + '-header-' + columnIndex,
        className: cx(classes.border, classes.cell)
      });
    })(emptyValues.length));
  }, totalMaxCount);
};
RowValues.propTypes = process.env.NODE_ENV !== "production" ? {
  id: PropTypes.string,
  rows: PropTypes.array,
  emptyValues: PropTypes.array
} : {};
var MyTable = function MyTable(_ref9) {
  var _ref9$header = _ref9.header,
    header = _ref9$header === undefined ? [] : _ref9$header,
    _ref9$sections = _ref9.sections,
    sections = _ref9$sections === undefined ? [] : _ref9$sections,
    _ref9$rows = _ref9.rows,
    rows = _ref9$rows === undefined ? [] : _ref9$rows,
    _ref9$labelRenderer = _ref9.labelRenderer,
    labelRenderer = _ref9$labelRenderer === undefined ? R.prop('name') : _ref9$labelRenderer;
  var classes = useStyles();
  var nbShowedValuesHeader = getMaxValuesLength(header);
  var emptyValues = getEmptyValues(nbShowedValuesHeader);
  var nbColumns = R.length(rows) + R.max(nbShowedValuesHeader, 1) + 1;
  var nbColumnRows = R.length(rows) + 1;
  var enhancedHeader = R.pipe(getLabel(labelRenderer))(header);
  var enhancedRows = R.pipe(getLabel(labelRenderer))(rows);
  var enhancedSections = R.pipe(getLabel(labelRenderer))(sections);
  return React.createElement(Table, {
    tabIndex: 0,
    'data-testid': 'table-preview-test-id',
    className: cx(classes.table, classes.border)
  }, React.createElement('tbody', null, R.isEmpty(enhancedHeader) ? null : React.createElement(Header, {
    colSpan: nbColumnRows,
    header: enhancedHeader,
    valuesSpan: nbShowedValuesHeader
  }), React.createElement(RowHeader, {
    rows: enhancedRows,
    emptyValues: emptyValues
  }), R.isEmpty(enhancedSections) ? null : React.createElement(Sections, {
    colSpan: nbColumns,
    sections: enhancedSections
  }), React.createElement(RowValues, {
    rows: enhancedRows,
    emptyValues: emptyValues
  })));
};
MyTable.propTypes = process.env.NODE_ENV !== "production" ? {
  header: PropTypes.arrayOf(PropTypes.shape(patern)),
  sections: PropTypes.arrayOf(PropTypes.shape(patern)),
  rows: PropTypes.arrayOf(PropTypes.shape(patern)),
  labelRenderer: PropTypes.func
} : {};
export default withBlank(function (_ref10) {
  var _ref10$rows = _ref10.rows,
    rows = _ref10$rows === undefined ? [] : _ref10$rows,
    isBlank = _ref10.isBlank;
  return {
    isBlank: R.or(isBlank, R.isEmpty(rows))
  };
})(MyTable);